import {formatDistanceToNow} from "date-fns";
import {rrulestr} from "rrule";

import {MaintenanceOccurrence, MaintenanceSchedule, MaintenanceScheduleState} from "@/lib/types/maintenance.ts";

type NextOccurrence = Omit<MaintenanceOccurrence, "schedule" | "services">;

export const getDurationDisplay = (minutes?: number) => {
  if (!minutes) return "—";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  return `${mins}m`;
};

// We do this because the backend takes a minute to generate the next occurrence after a schedule is created or updated,
// so we construct at best effort the next occurrence using information we have about the schedule on the frontend to ensure the UI is responsive and up-to-date
export const getNextOccurrence = (schedule: MaintenanceSchedule): null | NextOccurrence => {
  if (schedule.next_occurrence) return schedule.next_occurrence;
  if (schedule.schedule_type === "RRULE" && schedule.rrule) {
    const rrule = rrulestr(schedule.rrule);
    const nextRruleOccurrence = rrule.after(new Date());
    if (nextRruleOccurrence) {
      return constructOccurrence(
        schedule,
        nextRruleOccurrence.toISOString(),
        new Date(nextRruleOccurrence.getTime() + (schedule.duration_minutes || 0) * 60000).toISOString()
      );
    } else {
      return null;
    }
  }
  return null;
};

export const getScheduleState = (schedule: MaintenanceSchedule) => {
  if (!schedule.is_active) {
    return MaintenanceScheduleState.PAUSED;
  }
  if (schedule.is_in_maintenance) {
    return MaintenanceScheduleState.IN_MAINTENANCE;
  }
  if (!getNextOccurrence(schedule)) {
    return MaintenanceScheduleState.ENDED;
  }
  return MaintenanceScheduleState.ACTIVE;
};

export const getNextOccurrenceText = (schedule: MaintenanceSchedule) => {
  if (!schedule.is_active) {
    return "Schedule Paused";
  }
  const nextOccurrence = getNextOccurrence(schedule);
  if (!nextOccurrence) {
    return "Not Scheduled";
  }
  return formatDistanceToNow(new Date(nextOccurrence.starts_at), {addSuffix: true});
};

const constructOccurrence = (
  schedule: MaintenanceSchedule,
  starts_at: string,
  ends_at: string | null
): NextOccurrence => {
  return {
    id: -1,
    pause_checks_during_maintenance: schedule.pause_checks_during_maintenance,
    starts_at,
    ends_at,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    recurrence_id: "",
    is_exception: false,
  };
};
